/**
 * Pure logica van de Notities-pagina (Notion-achtige opzet, 25 sep 2026):
 * titel ↔ tekst, labels in de lijst, het "/"-menu en de boom-index.
 * Geen React, geen fetch — zodat het los te testen is.
 */

/** Titel uit de eerste regel van een tekst (zelfde regel als vroeger: max 50 tekens). */
export function titelUit(tekst) {
  const eerste = String(tekst || '').trim().split('\n')[0] || '';
  return eerste.replace(/\*\*/g, '').replace(/^(#{1,6}|[-*>])\s*/, '').trim().slice(0, 50) || 'Notitie';
}

const kaal = (s) => String(s || '').replace(/\*\*/g, '').replace(/^(#{1,6}|[-*>])\s*/, '').trim();

/**
 * Splitst een opgeslagen notitie in titel + tekst voor de pagina-weergave.
 * Oude notities hebben het onderwerp óók als eerste regel van de tekst (de titel wérd
 * de eerste regel). Die regel tonen we dan niet dubbel; alleen als hij echt gelijk is
 * (of, bij een op 50 tekens afgekapt onderwerp, ermee begint).
 */
export function splitsTitel(onderwerp, tekst) {
  const titel = String(onderwerp || '').trim();
  const t = String(tekst || '');
  if (!titel) return { titel: '', tekst: t };
  const regels = t.replace(/^\s+/, '').split('\n');
  const eerste = kaal(regels[0]);
  const gelijk = eerste === titel || (titel.length >= 50 && eerste.startsWith(titel));
  if (!gelijk) return { titel: titel === 'Notitie' ? '' : titel, tekst: t };
  return { titel, tekst: regels.slice(1).join('\n').replace(/^\s*\n/, '') };
}

/** Wat er naar de engine gaat: onderwerp + tekst (de engine wil altijd een tekst). */
export function naarOpslag(titel, tekst) {
  const t = String(tekst || '').trim();
  const kop = String(titel || '').trim();
  return {
    title: (kop || titelUit(t)).slice(0, 100),
    notes: t || kop,
  };
}

/** Datum kort in het Nederlands: "25 sep", met jaartal als het niet dit jaar is. */
export function korteDatum(waarde, nu = new Date()) {
  if (!waarde) return '';
  const d = new Date(String(waarde).length === 10 ? `${waarde}T12:00:00` : waarde);
  if (Number.isNaN(d.getTime())) return '';
  const opties = { day: 'numeric', month: 'short' };
  if (d.getFullYear() !== nu.getFullYear()) opties.year = 'numeric';
  return d.toLocaleDateString('nl-NL', opties).replace('.', '');
}

/** Id's van projecten (en hun account) uit de boom, voor labels en kiezers. */
export function maakBoomIndex(boom) {
  const projecten = new Map();
  (boom || []).forEach((acc) => (acc.projecten || []).forEach((p) => {
    projecten.set(p.id, { ...p, account: acc.account });
  }));
  return { projecten };
}

/** Project bij een notitie; project_id kan een lijst-als-tekst zijn ("['rec…']"). */
export function vindProject(index, projectId) {
  if (!projectId || !index) return null;
  const s = String(projectId);
  if (index.projecten.has(s)) return index.projecten.get(s);
  const m = s.match(/rec[a-zA-Z0-9]{14}/);
  return m ? index.projecten.get(m[0]) || null : null;
}

/** Stil label in de lijst: waar hoort de notitie bij? Leeg bij een snelle notitie. */
export function plekLabel(n, index) {
  if (n.projectId) {
    const p = vindProject(index, n.projectId);
    const nummer = n.projectnummer || p?.nummer;
    const naam = n.projectnaam || p?.naam;
    return [nummer, naam].filter(Boolean).join(' · ') || 'Project';
  }
  if (n.klant) return n.folder ? `${n.klant} · ${n.folder}` : n.klant;
  return '';
}

/** De plek van een notitie in de vorm die de pagina gebruikt. */
export function plekVanNotitie(n, index) {
  if (!n) return { type: 'los' };
  if (n.projectId) {
    const p = vindProject(index, n.projectId);
    return { type: 'project', projectId: p?.id || n.projectId, account: p?.account || n.klant || '' };
  }
  if (n.klant === 'Full Frame') return { type: 'fullframe' };
  if (n.klant) return { type: 'account', account: n.klant, folder: n.folder || '' };
  return { type: 'los' };
}

/** POST-/PATCH-velden bij een plek. */
export function plekVelden(plek) {
  if (!plek) return {};
  if (plek.type === 'project' && plek.projectId) return { projectId: plek.projectId };
  if (plek.type === 'fullframe') return { klantNaam: 'Full Frame' };
  if (plek.type === 'account' && plek.account) {
    return plek.folder ? { klantNaam: plek.account, folder: plek.folder } : { klantNaam: plek.account };
  }
  return {};
}

/** Body-velden voor een verplaatsing (PATCH met verplaats: true). "Los" heeft geen
 *  plekvelden, dus zegt het dat expliciet — zo kan ook terug naar Snelle notities
 *  (het ongedaan maken van los → project/account). */
export function verplaatsVelden(plek) {
  if (!plek || plek.type === 'los') return { los: true, verplaats: true };
  return { ...plekVelden(plek), verplaats: true };
}

/** Meeting die al in de tekst staat (via het "Open meeting"-blok). */
export function meetingUitTekst(tekst) {
  const s = String(tekst || '');
  const link = s.match(/\/MeetingDashboard\/([\w-]+)/);
  if (!link) return null;
  const kop = s.match(/###\s*🎙️\s*Meeting:\s*(.+?)(?:\s+—\s+[^\n]*)?\n/);
  return { id: link[1], titel: kop ? kop[1].trim() : 'Meeting' };
}

/** Het meeting-blok dat in de tekst komt (ongewijzigd formaat, ook voor de projectweergave). */
export function meetingBlok(m) {
  const datum = m.datum ? new Date(m.datum).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  return `### 🎙️ Meeting: ${m.titel}${datum ? ` — ${datum}` : ''}\n${m.samenvatting || '_Nog geen samenvatting._'}\n\n[Open meeting](/MeetingDashboard/${m.id})`;
}

/** Tekst achter een blok aanplakken (sjabloon, meeting). */
export function voegBlokToe(tekst, blok) {
  const t = String(tekst || '');
  return t.trim() ? `${t.replace(/\s+$/, '')}\n\n${blok}` : blok;
}

// ── Sjablonen (uit het OneNote-template "ALGEMEEN", maart 2026) ──
const datumNL = () => new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
const regels = (velden) => velden.map(([k, v]) => `**${k}** ${v || ''}`).join('  \n');
export const SJABLONEN = [
  { key: 'basis', label: 'Intake klant – film basis', maak: ({ klant, pl }) => `## Intake klant – film basis\n${regels([
    ['Klantnaam:', klant], ['Contactpersoon:', ''], ['FF projectleider:', pl], ['Doel:', ''], ['Doelgroep:', ''],
    ['Deliverables / kanalen:', ''], ['Beoogde lengte:', ''], ['Datum draaidag:', ''], ['Gewenste deadline:', ''],
    ['Script by FF?', ''], ['Regie by FF?', ''], ['Budgetindicatie:', ''],
  ])}` },
  { key: 'uitgebreid', label: 'Intake klant – uitgebreid', maak: () => `## Intake klant – uitgebreid\n${regels([
    ['Aanleiding van content?', ''], ['Hoofdboodschap:', ''], ['Welke emotie?', ''], ['Tone of voice:', ''], ['Call to action:', ''],
    ['Randvoorwaarden oplevering:', 'ondertiteling? · formaten? · social versies?'], ['Quitclaim nodig?', ''], ['Locaties by FF?', ''], ['Cast by FF?', ''],
  ])}` },
  { key: 'aantekeningen', label: 'Aantekeningen gesprek', maak: ({ pl }) => `## Aantekeningen – ${pl || ''}\n${regels([
    ['Aanwezig:', ''], ['Datum:', datumNL()],
  ])}\n\n[Inspiratievragen (wiki)](https://wiki.fullframe.nl/doc/briefing-gesprekken-j2g3dpkXcQ)\n\n**Notes:**\n\n**Doel en doelgroep:**\n\n**Stijl:**\n\n**Deliverables:**\n\n**Planning:**\n\n**Aanpak:**\n` },
  { key: 'alles', label: 'Volledig template', maak: (ctx) => [SJABLONEN[0].maak(ctx), SJABLONEN[1].maak(ctx), SJABLONEN[2].maak(ctx)].join('\n\n') },
];

// ── "/"-menu ──
export const SLASH_ITEMS = [
  { key: 'tekst', label: 'Tekst', hint: 'gewone alinea', woorden: ['paragraaf', 'text'] },
  { key: 'kop', label: 'Kop', hint: '##', woorden: ['heading', 'h2', 'titel'] },
  { key: 'subkop', label: 'Subkop', hint: '###', woorden: ['heading', 'h3'] },
  { key: 'lijst', label: 'Opsomming', hint: '-', woorden: ['lijst', 'bullet', 'ul'] },
  { key: 'genummerd', label: 'Genummerde lijst', hint: '1.', woorden: ['lijst', 'nummer', 'ol'] },
  { key: 'todo', label: 'To-do', hint: '☐', woorden: ['todo', 'taak', 'checkbox', 'vinkje', 'actiepunt'] },
  { key: 'citaat', label: 'Citaat', hint: '>', woorden: ['quote'] },
  { key: 'lijn', label: 'Scheidingslijn', hint: '---', woorden: ['divider', 'hr'] },
  { key: 'afbeelding', label: 'Afbeelding', hint: 'upload', woorden: ['foto', 'plaatje', 'image'] },
  { key: 'meeting', label: 'Meeting koppelen', hint: 'FF Recorder', woorden: ['opname', 'recorder'] },
  ...SJABLONEN.map((s) => ({ key: `sjabloon:${s.key}`, label: s.label, hint: 'sjabloon', woorden: ['sjabloon', 'template', 'intake'] })),
];

/** Filter het "/"-menu op wat er na de slash getypt is (begin van een woord telt). */
export function filterSlash(query, items = SLASH_ITEMS) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return items;
  const begint = (s) => String(s).toLowerCase().split(/[\s–-]+/).some((w) => w.startsWith(q));
  return items.filter((it) => begint(it.label) || (it.woorden || []).some((w) => w.startsWith(q)) || it.label.toLowerCase().includes(q));
}

/** Staat er vlak voor de cursor een "/"-opdracht? Geeft de query terug, of null. */
export function slashQuery(tekstVoorCursor) {
  const m = String(tekstVoorCursor || '').match(/(?:^|\s)\/([\p{L}\d-]{0,20})$/u);
  return m ? m[1] : null;
}

/** ☐ ↔ ☑ */
export const VINKJE_OPEN = '☐';
export const VINKJE_DICHT = '☑';
export const wisselVinkje = (c) => (c === VINKJE_OPEN ? VINKJE_DICHT : c === VINKJE_DICHT ? VINKJE_OPEN : c);

/** Is dit een getypte toets die "nieuwe notitie" mag betekenen (N buiten tekstvelden)? */
export function isNieuwSneltoets(e, actiefElement) {
  if (!e || e.metaKey || e.ctrlKey || e.altKey) return false;
  if (String(e.key).toLowerCase() !== 'n') return false;
  const tag = String(actiefElement?.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return false;
  return !actiefElement?.isContentEditable;
}
